import React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import { calculateUpdatedPrice, useBulkUpdatePrices } from "Catalog/Grid/shared/components/BulkUpdatePrices"
import { useGenerateRefs } from "Catalog/Grid/shared/components/GenerateRefs"
import GridLayout from "Catalog/Grid/shared/components/GridLayout"
import GridTable from "Catalog/Grid/shared/components/GridTable"
import ManageVariants from "Catalog/Grid/shared/components/ManageVariants"
import CatalogToolbar from "Catalog/shared/components/CatalogToolbar"
import { useStatefulFieldRemount } from "Catalog/shared/components/StatefulFieldRemountContext"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useFindItemOrRedirect from "Catalog/shared/hooks/useFindItemOrRedirect"
import useKeyboardNavigation from "Catalog/shared/hooks/useKeyboardNavigation"
import useEditor from "shared/hooks/useEditor"
import { IProduct, ISku, isProduct, IVariant } from "shared/hubrise/models"
import { sortedCategoriesAndProducts } from "shared/hubrise/utils/Product"
import * as Restrictions from "shared/hubrise/utils/Restrictions"
import * as Sku from "shared/hubrise/utils/Sku"

type Item = { id: number; product: IProduct; sku: ISku }

const Skus = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams<{ skuId: string }>()

  const { editor, catalog } = useEditor()
  const { remount } = useStatefulFieldRemount()

  const { catalogNavigation, visit } = useCatalogNavigation()
  const indexPath = catalogNavigation.gridPath("skus")

  const toolbar = <CatalogToolbar commandActions={<ManageVariants />} />

  const items: Array<Item> = React.useMemo(
    () =>
      sortedCategoriesAndProducts(catalog)
        .filter((item) => isProduct(item))
        .flatMap((product) => product.skus.map((sku) => ({ id: sku.id, product, sku }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, editor.catalogHash],
  )

  const selectedItem = useFindItemOrRedirect<Item>(
    params.skuId,
    (id) => items.find((item) => item.id === id),
    indexPath,
  )

  const removeAllRefs = () => {
    editor.sku.removeAllRefs()
    remount()
  }

  const { generateRefsModal, openGenerateRefs } = useGenerateRefs("sku", (options) => {
    editor.sku.generateRefs(options)
    remount()
  })

  const enableAll = (enabled: boolean, variantRef?: string) => {
    editor.sku.enableAll(enabled, variantRef)
    remount()
  }

  const { bulkUpdatePricesModal, openBulkUpdatePrices } = useBulkUpdatePrices("skus", (variant, rules) => {
    editor.sku.bulkUpdatePrices(
      rules.sourceVariantRef,
      variant?.ref ?? null,
      rules.skipAlreadySetPrices,
      rules.categoryIds,
      (price) => calculateUpdatedPrice(price, rules),
    )
    remount()
  })

  const clearAllPrices = (variant: IVariant) => {
    editor.sku.clearAllPrices(variant.ref)
    remount()
  }

  React.useEffect(() => {
    visit("sku", selectedItem?.sku)
  }, [selectedItem, visit])

  useKeyboardNavigation<Item>({
    item: selectedItem,
    collection: items,
    indexPath: () => indexPath,
    showPath: (item) => catalogNavigation.gridPath("sku", item.sku.id),
  })

  const main =
    items.length > 0 ? (
      <>
        {bulkUpdatePricesModal}
        {generateRefsModal}

        <GridTable
          items={items}
          selectedItem={selectedItem}
          designatorsHeaders={[t("grid.category"), t("grid.sku")]}
          removeAllRefs={removeAllRefs}
          openGenerateRefs={openGenerateRefs}
          variants={catalog.data.variants}
          enableAll={enableAll}
          openBulkUpdatePrices={openBulkUpdatePrices}
          clearAllPrices={clearAllPrices}
          selectItemPath={({ sku }) => catalogNavigation.gridPath("sku", sku.id)}
          editItemPath={(item) => catalogNavigation.editPath("product", item.product.id)}
          previewItemPath={(item) => catalogNavigation.previewPath("product", item.product.id)}
          designators={({ product, sku }) => [
            catalog.data.categories.find((c) => c.id === product.categoryId)!.name,
            Sku.fullName(product, sku),
          ]}
          itemRef={({ sku }) => sku.ref}
          setRef={({ product, sku }, ref) => editor.sku.update(product.id, sku.id, "ref", ref)}
          catalogPrice={({ sku }) => sku.price}
          setCatalogPrice={({ product, sku }, price) => editor.sku.update(product.id, sku.id, "price", price)}
          variantPrice={({ sku }, variantRef) => Sku.variantPrice(sku, variantRef)}
          setVariantPrice={({ product, sku }, variantRef, price) =>
            editor.sku.updateVariantPrice(product.id, sku.id, variantRef, price)
          }
          isEnabled={({ sku }, variantRef) => Restrictions.isEnabled(sku.restrictions, variantRef)}
          enable={({ product, sku }, enabled, variantRef) => {
            editor.sku.enable(product.id, sku.id, enabled, variantRef)
          }}
        />
      </>
    ) : null

  return <GridLayout toolbar={toolbar} main={main} none={t("product.none")} testId="grid-products" />
}

export default Skus
